import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";
import { getQuestionGames } from "../../services/questionGameService";
import { createGameResult } from "../../services/gameResultService";

export const AddGameResultLayer = () => {
  const [leagues, setLeagues] = useState([]);
  const [matches, setMatches] = useState([]);
  const [games, setGames] = useState([]);

  const [leagueId, setLeagueId] = useState("");
  const [matchId, setMatchId] = useState("");

  const [filteredMatches, setFilteredMatches] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);

  const [winners, setWinners] = useState({});
  const [loading, setLoading] = useState(false);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    const loadData = async () => {
      try {
        const [leagueRes, matchRes, gameRes] = await Promise.all([
          getLeagues(),
          getMatches(),
          getQuestionGames(),
        ]);

        setLeagues(leagueRes?.data || []);
        setMatches(matchRes?.data || []);

        // 🔥 HIDE COMPLETED STATUS
        const formattedGames = (gameRes?.data || [])
          .filter((g) => g.status !== "completed") // 🚀 Condition Added
          .map((g) => {
            let parsedOptions = [];

            if (Array.isArray(g.ans_options)) {
              parsedOptions = g.ans_options;
            } else if (Array.isArray(g.options)) {
              parsedOptions = g.options;
            } else if (g.options_json) {
              try {
                parsedOptions = JSON.parse(g.options_json);
              } catch {
                parsedOptions = [];
              }
            }

            return {
              ...g,
              safe_options: parsedOptions,
            };
          });

        setGames(formattedGames);
      } catch {
        toast.error("Failed to load data");
      }
    };

    loadData();
  }, []);

  /* ================= FILTER MATCH ================= */
  useEffect(() => {
    if (!leagueId) {
      setFilteredMatches([]);
      setMatchId("");
      return;
    }

    const list = matches.filter(
      (m) => String(m.league_id) === String(leagueId)
    );

    setFilteredMatches(list);
    setMatchId("");
    setFilteredGames([]);
  }, [leagueId, matches]);

  /* ================= FILTER GAMES ================= */
  useEffect(() => {
    if (!matchId) {
      setFilteredGames([]);
      return;
    }

    const list = games.filter(
      (g) => String(g.match_id) === String(matchId)
    );

    setFilteredGames(list);
    setWinners({});
  }, [matchId, games]);

  /* ================= HANDLE WINNER ================= */
  const handleWinnerChange = (gameId, optionId) => {
    setWinners((prev) => ({
      ...prev,
      [gameId]: optionId,
    }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmitAll = async () => {
    if (!leagueId || !matchId) {
      toast.error("Select League and Match");
      return;
    }

    const settlements = filteredGames.map((g) => ({
      game_id: Number(g.id),
      match_id: Number(matchId),
      winning_option_id: Number(winners[g.id]),
    }));

    if (settlements.some((s) => !s.winning_option_id)) {
      toast.error("Select winner for all games");
      return;
    }

    setLoading(true);

    try {
      const res = await createGameResult({
        league_id: leagueId,
        settlements,
      });

      if (res?.status) {
        toast.success("Results Declared Successfully");
        setWinners({});
      } else {
        toast.error(res?.message || "Failed");
      }
    } catch {
      toast.error("Server Error");
    }

    setLoading(false);
  };

  return (
    <div className="container-fluid mt-4">
      <div style={{padding:"30px"}} className="card shadow-sm">

        <h5 className="mb-4">Declare Game Results</h5>

        {/* LEAGUE + MATCH */}
        <div className="row mb-3">
          <div className="col-md-6">
            <label>League</label>
            <select
              className="form-select"
              value={leagueId}
              onChange={(e) => setLeagueId(e.target.value)}
            >
              <option value="">Select League</option>
              {leagues.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.league_name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label>Match</label>
            <select
              className="form-select"
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
              disabled={!leagueId}
            >
              <option value="">Select Match</option>
              {filteredMatches.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.team_a} vs {m.team_b}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* TABLE */}
        {filteredGames.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Game Title</th>
                    <th>Winning Option</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGames.map((g, index) => (
                    <tr key={g.id}>
                      <td>{index + 1}</td>
                      <td>{g.game_title}</td>
                      <td>
                        <select
                          className="form-select"
                          value={winners[g.id] || ""}
                          onChange={(e) =>
                            handleWinnerChange(g.id, e.target.value)
                          }
                        >
                          <option value="">Select Winner</option>
                          {g.safe_options?.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name || opt.option}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              className="btn btn-dark w-100 mt-3"
              onClick={handleSubmitAll}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit All Results"}
            </button>
          </>
        )}

        {matchId && filteredGames.length === 0 && (
          <div className="text-center text-muted mt-3">
            No Available Questions
          </div>
        )}

      </div>
    </div>
  );
};